import { AlertTriangle } from 'lucide-react'

export const SECTION_CONFIG = {
  fulfillment: {
    apiSection: 'fulfillment',
    title: ['履约物流', 'Fulfillment logistics'],
    description: ['查看已付款订单的履约状态。', 'Review fulfillment status for paid orders.'],
    empty: ['暂无履约记录。', 'No fulfillment records.'],
  },
  tracking: {
    apiSection: 'tracking',
    title: ['物流追踪', 'Shipment tracking'],
    description: ['查看订单的承运商、追踪号和当前状态。', 'Review carriers, tracking numbers and current shipment status.'],
    empty: ['暂无物流记录。', 'No tracking records.'],
  },
  exceptions: {
    apiSection: 'exceptions',
    title: ['物流异常 / 审计', 'Logistics exceptions / audit'],
    description: ['现有接口返回 Commerce 审计事件；数据未必都是物流异常。', 'The current endpoint returns Commerce audit events; not every event is necessarily a logistics exception.'],
    empty: ['暂无审计记录。', 'No audit records.'],
  },
}

export const ORDER_STATUS_LABELS = {
  paid: ['已付款', 'Paid'],
  fulfillment: ['履约中', 'In fulfillment'],
  completed: ['已完成', 'Completed'],
  partially_refunded: ['部分退款', 'Partially refunded'],
  refunded: ['已退款', 'Refunded'],
  pending_payment: ['待付款', 'Pending payment'],
  cancelled: ['已取消', 'Cancelled'],
  draft: ['草稿', 'Draft'],
}

export const TRACKING_STATUS_LABELS = {
  pending: ['待处理', 'Pending'],
  pre_transit: ['待发出', 'Pre-transit'],
  label_created: ['已创建面单', 'Label created'],
  returned: ['已退回', 'Returned'],
  in_transit: ['运输中', 'In transit'],
  delivered: ['已送达', 'Delivered'],
  exception: ['异常', 'Exception'],
}

export function text(value) {
  if (value == null || value === '') return ''
  return String(value)
}

export function firstValue(...values) {
  return values.map(text).find(Boolean) || ''
}

export function orderFrom(record) {
  return record?.order && typeof record.order === 'object' ? record.order : record
}

export function orderId(order) {
  return firstValue(order?.number, order?.id)
}

export function customerLabel(order) {
  const customer = order?.customer || {}
  return firstValue(customer.name, customer.displayName, customer.email, customer.sub, customer.id)
}

export function statusLabel(value, labels, lang) {
  const key = text(value).toLowerCase()
  const label = labels[key]
  return label ? label[lang === 'zh' ? 0 : 1] : (text(value) || '—')
}

export function trackingException(order) {
  const exception = order?.trackingException ?? order?.exception
  if (!exception) return ''
  if (typeof exception === 'string') return exception
  return firstValue(exception.message, exception.code, JSON.stringify(exception))
}

export function formatDate(value, lang) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return text(value)
  return new Intl.DateTimeFormat(lang === 'zh' ? 'zh-CN' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export function formatAmount(amount, currency, lang) {
  if (amount == null || amount === '') return '—'
  const numeric = Number(amount)
  if (!Number.isFinite(numeric)) return text(amount)
  if (!currency) return `${numeric} ${lang === 'zh' ? '最小货币单位' : 'minor units'}`
  try {
    const formatter = new Intl.NumberFormat(lang === 'zh' ? 'zh-CN' : 'en-US', {
      style: 'currency',
      currency: String(currency).toUpperCase(),
    })
    return formatter.format(numeric / (10 ** formatter.resolvedOptions().maximumFractionDigits))
  } catch {
    return `${numeric} ${currency} ${lang === 'zh' ? '最小货币单位' : 'minor units'}`
  }
}

export function errorMessage(error, lang) {
  const message = text(error?.message)
  if (message) return message
  return lang === 'zh' ? '请求失败，请稍后重试。' : 'The request failed. Try again later.'
}

export function orderRow(record) {
  const order = orderFrom(record)
  return {
    source: record,
    order,
    key: firstValue(order?.id, order?.number) || JSON.stringify(order),
    orderId: orderId(order),
    customer: customerLabel(order),
    orderStatus: text(order?.status),
    fulfillmentStatus: text(order?.fulfillmentStatus),
    trackingStatus: text(order?.trackingStatus),
    trackingNumber: text(order?.trackingNumber),
    carrier: text(order?.carrier),
    provider: text(order?.logisticsProvider),
    exception: trackingException(order),
    updatedAt: order?.updatedAt || order?.fulfillmentUpdatedAt || order?.lastEventAt,
  }
}

export function exceptionRow(record, index) {
  const details = record?.details && typeof record.details === 'object' ? record.details : {}
  return {
    source: record,
    key: firstValue(record?.id, record?.entityId) || `exception-${index}`,
    orderId: firstValue(record?.orderId, details.orderId),
    action: firstValue(record?.action, record?.type),
    result: firstValue(record?.result, details.result),
    message: firstValue(details.message, details.error, details.reason, record?.message),
    createdAt: record?.createdAt || record?.created_at,
    actor: firstValue(record?.actorId, record?.actorType),
  }
}

export function StatusPill({ value, labels, tone = 'neutral', lang }) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    good: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200',
    warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
    danger: 'bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-200',
  }
  return <span className={`inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-xs font-bold ${tones[tone] || tones.neutral}`}>{statusLabel(value, labels, lang)}</span>
}

export function toneForTracking(status, exception) {
  if (exception || status === 'exception') return 'danger'
  if (status === 'delivered') return 'good'
  if (status === 'in_transit' || status === 'pre_transit') return 'warning'
  return 'neutral'
}

export function DetailField({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-1 break-words text-sm text-slate-800 dark:text-slate-100">{value || '—'}</dd>
    </div>
  )
}

export function locationLabel(location) {
  if (!location) return ''
  if (typeof location === 'string') return location
  return firstValue(
    location.label,
    [location.city, location.state || location.region, location.country, location.zip].filter(Boolean).join(', '),
  )
}

export function OrderAggregateDetails({ row, aggregate, lang }) {
  const order = orderFrom(aggregate)
  const fulfillments = Array.isArray(aggregate?.fulfillments) ? aggregate.fulfillments : []
  const orderException = trackingException(order)
  const auditEvents = (Array.isArray(aggregate?.auditLogs) ? aggregate.auditLogs : [])
    .filter((entry) => /^(tracking|fulfillment|shipment|package)\./i.test(text(entry?.action)))

  return (
    <>
      <div className="space-y-4 border border-slate-200 p-5 dark:border-slate-800">
        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">{lang === 'zh' ? '订单物流详情' : 'Order logistics details'}</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{lang === 'zh' ? '详情来自该订单的 Commerce aggregate。' : 'Details are loaded from this order’s Commerce aggregate.'}</p>
        </div>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField label={lang === 'zh' ? '订单号' : 'Order'} value={firstValue(order?.number, order?.id, row.orderId)} />
          <DetailField label={lang === 'zh' ? '客户' : 'Customer'} value={customerLabel(order)} />
          <DetailField label={lang === 'zh' ? '订单状态' : 'Order status'} value={statusLabel(order?.status, ORDER_STATUS_LABELS, lang)} />
          <DetailField label={lang === 'zh' ? '更新时间' : 'Updated'} value={formatDate(order?.updatedAt, lang)} />
          <DetailField label={lang === 'zh' ? '物流服务商' : 'Logistics provider'} value={firstValue(...fulfillments.map((item) => item?.provider), row.provider)} />
          <DetailField label={lang === 'zh' ? '订单金额' : 'Order total'} value={formatAmount(order?.amounts?.total, order?.currency, lang)} />
        </dl>
        {orderException && (
          <div className="flex gap-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
            <AlertTriangle size={17} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{orderException}</span>
          </div>
        )}
      </div>

      <section className="space-y-3">
        <h3 className="font-bold text-slate-900 dark:text-white">{lang === 'zh' ? `履约 / Shipment（${fulfillments.length}）` : `Fulfillment / shipments (${fulfillments.length})`}</h3>
        {fulfillments.length === 0 && (
          <p className="border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
            {lang === 'zh' ? '该订单详情未返回履约记录。' : 'No fulfillment records were returned for this order.'}
          </p>
        )}
        {fulfillments.map((fulfillment, fulfillmentIndex) => {
          const packages = Array.isArray(fulfillment?.packages) ? fulfillment.packages : []
          const fulfillmentItems = Array.isArray(fulfillment?.items) ? fulfillment.items : []
          return (
            <article key={fulfillment.id || `fulfillment-${fulfillmentIndex}`} className="space-y-4 border border-slate-200 p-4 dark:border-slate-800">
              <dl className="grid gap-4 sm:grid-cols-2">
                <DetailField label={lang === 'zh' ? '履约 ID' : 'Fulfillment ID'} value={fulfillment.id} />
                <DetailField label={lang === 'zh' ? '服务商' : 'Provider'} value={fulfillment.provider} />
                <DetailField label={lang === 'zh' ? '服务商单号' : 'Provider shipment ID'} value={fulfillment.providerId} />
                <DetailField label={lang === 'zh' ? '履约状态' : 'Fulfillment status'} value={statusLabel(fulfillment.status, TRACKING_STATUS_LABELS, lang)} />
                <DetailField label={lang === 'zh' ? '创建时间' : 'Created'} value={formatDate(fulfillment.createdAt, lang)} />
                <DetailField label={lang === 'zh' ? '更新时间' : 'Updated'} value={formatDate(fulfillment.updatedAt, lang)} />
              </dl>

              {fulfillmentItems.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{lang === 'zh' ? '履约商品' : 'Fulfilled items'}</h4>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-200">
                    {fulfillmentItems.map((item, itemIndex) => {
                      const orderItem = (order?.items || []).find((candidate) => candidate.id === item.orderItemId)
                      const itemLabel = firstValue(orderItem?.name, orderItem?.sku, item.orderItemId)
                      return <li key={item.id || item.orderItemId || itemIndex}>{itemLabel || '—'}{item.quantity != null ? ` × ${item.quantity}` : ''}</li>
                    })}
                  </ul>
                </div>
              )}

              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{lang === 'zh' ? `包裹（${packages.length}）` : `Packages (${packages.length})`}</h4>
                {packages.length === 0 && <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'zh' ? '详情未返回包裹。' : 'No packages were returned.'}</p>}
                {packages.map((parcel, packageIndex) => {
                  const events = Array.isArray(parcel?.events) ? parcel.events : []
                  const exception = trackingException({ exception: parcel?.exception })
                  return (
                    <div key={parcel.id || `package-${packageIndex}`} className="space-y-3 rounded-md bg-slate-50 p-3 dark:bg-slate-900/70">
                      <dl className="grid gap-3 sm:grid-cols-2">
                        <DetailField label={lang === 'zh' ? '包裹 ID' : 'Package ID'} value={parcel.id} />
                        <DetailField label={lang === 'zh' ? '承运商' : 'Carrier'} value={parcel.carrier} />
                        <DetailField label={lang === 'zh' ? '追踪号' : 'Tracking number'} value={parcel.trackingNumber} />
                        <DetailField label={lang === 'zh' ? '包裹状态' : 'Package status'} value={statusLabel(parcel.status, TRACKING_STATUS_LABELS, lang)} />
                        <DetailField label={lang === 'zh' ? '预计送达' : 'Estimated delivery'} value={formatDate(parcel.estimatedDeliveryAt, lang)} />
                        <DetailField label={lang === 'zh' ? '实际送达' : 'Delivered'} value={formatDate(parcel.deliveredAt, lang)} />
                        <DetailField label={lang === 'zh' ? '最近物流事件' : 'Last tracking event'} value={formatDate(parcel.lastEventAt, lang)} />
                      </dl>
                      {exception && (
                        <div className="flex gap-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
                          <AlertTriangle size={17} className="mt-0.5 shrink-0" aria-hidden="true" />
                          <span>{exception}</span>
                        </div>
                      )}
                      <div>
                        <h5 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{lang === 'zh' ? `物流事件（${events.length}）` : `Tracking events (${events.length})`}</h5>
                        {events.length === 0 && <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{lang === 'zh' ? '尚无物流事件。' : 'No tracking events recorded.'}</p>}
                        {events.length > 0 && (
                          <ol className="mt-2 space-y-2 border-l border-slate-300 pl-3 dark:border-slate-700">
                            {events.map((event, eventIndex) => (
                              <li key={event.id || event.providerEventId || `event-${eventIndex}`} className="text-sm">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  <StatusPill value={event.status} labels={TRACKING_STATUS_LABELS} tone={toneForTracking(event.status, false)} lang={lang} />
                                  {event.eventCode && <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{event.eventCode}</span>}
                                  <time className="text-xs text-slate-500 dark:text-slate-400">{formatDate(event.occurredAt, lang)}</time>
                                </div>
                                {event.description && <p className="mt-1 text-slate-800 dark:text-slate-100">{event.description}</p>}
                                {locationLabel(event.location) && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{locationLabel(event.location)}</p>}
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </article>
          )
        })}
      </section>

      {auditEvents.length > 0 && (
        <section className="space-y-3 border border-slate-200 p-4 dark:border-slate-800">
          <h3 className="font-bold text-slate-900 dark:text-white">{lang === 'zh' ? '物流审计事件' : 'Logistics audit events'}</h3>
          <ol className="space-y-3">
            {auditEvents.map((event, index) => (
              <li key={event.id || `audit-${index}`} className="border-l-2 border-amber-400 pl-3 text-sm">
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">{event.action || '—'}</span>
                  <time className="text-xs text-slate-500 dark:text-slate-400">{formatDate(event.createdAt, lang)}</time>
                  {event.actorType && <span className="text-xs text-slate-500 dark:text-slate-400">{event.actorType}{event.actorId ? `: ${event.actorId}` : ''}</span>}
                </div>
                {event.details && Object.keys(event.details).length > 0 && <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words text-xs text-slate-600 dark:text-slate-300">{JSON.stringify(event.details, null, 2)}</pre>}
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  )
}


// Same predicates as the legacy Commerce Exceptions page. Audit activity is not
// evidence of an unresolved exception; only the order summary fields qualify.
export function orderException(record) {
  const order = record?.order || record
  const payment = order?.paymentStatus === 'failed'
  const logistics = Boolean(order?.trackingException || order?.trackingStatus === 'exception' || order?.fulfillmentStatus === 'exception')
  return { order, payment, logistics }
}
